import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useRestaurantContext } from "@/contexts/RestaurantContext";
import type { Tables, TablesInsert, TablesUpdate } from "@/integrations/supabase/types";
import { toast } from "sonner";
import { useOperationLogs } from "./useOperationLogs";
import { cacheCategories, getCachedCategories } from "@/lib/pdv-cache";
import { getErrorMessage, logError } from "@/lib/error-messages";
import { getCategories } from "@/lib/dataGateway";

type Category = Tables<"categories">;
type CategoryInsert = TablesInsert<"categories">;
type CategoryUpdate = TablesUpdate<"categories">;

export function useCategories() {
  const { activeRestaurant } = useRestaurantContext();
  const [categories, setCategories] = useState<Category[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { logOperation } = useOperationLogs();

  const fetchCategories = useCallback(async () => {
    if (!activeRestaurant) {
      setIsLoading(false);
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Try to fetch from network first (via DataGateway: local server -> Supabase)
      if (navigator.onLine) {
        const { data, error, source } = await getCategories<Category>(activeRestaurant.id);
        if (import.meta.env.DEV && !error) {
          console.debug(`[useCategories] fonte: ${source}`);
        }

        if (error) {
          // Network error - try cache
          const cached = await getCachedCategories<Category>(activeRestaurant.id);
          if (cached) {
            setCategories(cached);
            toast.info("Usando categorias do cache offline");
          } else {
            toast.error("Erro ao carregar categorias");
          }
        } else {
          setCategories(data || []);
          // Cache for offline use (fire-and-forget to prevent blocking)
          cacheCategories(activeRestaurant.id, data || []).catch(() => {});
        }
      } else {
        // Offline mode - use cache
        const cached = await getCachedCategories<Category>(activeRestaurant.id);
        if (cached) {
          setCategories(cached);
        } else {
          toast.error("Sem conexão e sem cache de categorias");
        }
      }
    } catch (error) {
      // Ensure we never get stuck in loading state
      console.error("Error fetching categories:", error);
    } finally {
      setIsLoading(false);
    }
  }, [activeRestaurant]);

  const createCategory = async (data: Omit<CategoryInsert, "restaurant_id">) => {
    if (!activeRestaurant) return null;

    const maxOrder = categories.length > 0 
      ? Math.max(...categories.map(c => c.sort_order || 0)) 
      : 0;

    const { data: newCategory, error } = await supabase
      .from("categories")
      .insert({
        ...data,
        restaurant_id: activeRestaurant.id,
        sort_order: maxOrder + 1,
      })
      .select()
      .single();

    if (error) {
      toast.error(getErrorMessage(error, "criar", "category"));
      logError(error, "useCategories.createCategory");
      return null;
    }

    await logOperation("create", "category", newCategory.id, {
      name: newCategory.name,
    });

    toast.success("Categoria criada com sucesso!");
    await fetchCategories();
    return newCategory;
  };

  const updateCategory = async (id: string, data: CategoryUpdate) => {
    const currentCategory = categories.find(c => c.id === id);
    
    const { error } = await supabase
      .from("categories")
      .update(data)
      .eq("id", id);

    if (error) {
      toast.error(getErrorMessage(error, "atualizar", "category"));
      logError(error, "useCategories.updateCategory");
      return false;
    }

    await logOperation("update", "category", id, {
      name: data.name || currentCategory?.name,
      changes: {
        ...(data.name && data.name !== currentCategory?.name ? { name: { old: currentCategory?.name, new: data.name } } : {}),
        ...(data.is_active !== undefined && data.is_active !== currentCategory?.is_active ? { is_active: { old: currentCategory?.is_active, new: data.is_active } } : {}),
      },
    });

    toast.success("Categoria atualizada!");
    await fetchCategories();
    return true;
  };

  const deleteCategory = async (id: string) => {
    const category = categories.find(c => c.id === id);
    
    const { error } = await supabase
      .from("categories")
      .delete()
      .eq("id", id);

    if (error) {
      toast.error(getErrorMessage(error, "excluir", "category"));
      logError(error, "useCategories.deleteCategory");
      return false;
    }

    await logOperation("delete", "category", id, {
      name: category?.name,
    });

    toast.success("Categoria excluída!");
    await fetchCategories();
    return true;
  };

  const reorderCategories = async (reorderedCategories: Category[]) => {
    const updates = reorderedCategories.map((cat, index) => ({
      id: cat.id,
      sort_order: index + 1,
    }));

    for (const update of updates) {
      await supabase
        .from("categories")
        .update({ sort_order: update.sort_order })
        .eq("id", update.id);
    }

    setCategories(reorderedCategories.map((cat, index) => ({
      ...cat,
      sort_order: index + 1,
    })));
    
    toast.success("Ordem atualizada!");
  };

  return {
    categories,
    isLoading,
    fetchCategories,
    createCategory,
    updateCategory,
    deleteCategory,
    reorderCategories,
  };
}
